<script setup lang="ts">
import {computed} from 'vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {IconWeightType} from '../enum/IconWeightType'
import {IconWeightTypeToClassNameMap} from '../maps/IconWeightTypeToClassNameMap'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {IconBehavior} from '@toolify/client/src/modules/ui/enum/IconBehavior'
import {ColorVariantType} from '../enum/ColorVariantType'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {useFontAwesomeIcon} from '../composables/useFontAwesomeIcon'

interface Props {
  name: IconNameType | null
  weightType?: IconWeightType
  size?: SizeVariantType
  color?: ColorVariantType | null
  behavior?: IconBehavior
}

const props = withDefaults(defineProps<Props>(), {
  size: SizeVariantType.Medium,
  behavior: IconBehavior.None,
  color: ColorVariantType.Neutral,
})

const {getThemeVariable} = useThemeVariables()
const resolvedWeightType = computed(() => {
  return props.weightType ?? getThemeVariable.value(ThemeVariableType.PreferredIconWeightType)
})
const iconPrefix = computed(() => IconWeightTypeToClassNameMap.get(resolvedWeightType.value))
const {isIconRegistered} = useFontAwesomeIcon(iconPrefix, () => props.name)
</script>

<template>
  <div :class="['icon', `size-${props.size}`, props.color ? `color-${props.color}` : null]">
    <FontAwesomeIcon
      v-if="props.name && isIconRegistered"
      :icon="[iconPrefix, props.name]"
      :spin="props.behavior === IconBehavior.Spin"
      :class="['icon-content']"
    />
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.icon {
  display: flex;
  justify-content: center;
  align-items: center;

  .icon-content {
    max-width: 100%;
    max-height: 100%;
  }

  &.size-large {
    width: map-get(variables.$iconSizes, large);
    height: map-get(variables.$iconSizes, large);
    font-size: map-get(variables.$iconSizes, large);
  }

  &.size-standard {
    width: map-get(variables.$iconSizes, standard);
    height: map-get(variables.$iconSizes, standard);
    font-size: map-get(variables.$iconSizes, standard);
    }

  &.size-medium {
    width: map-get(variables.$iconSizes, medium);
    height: map-get(variables.$iconSizes, medium);
    font-size: calc(map-get(variables.$iconSizes, medium) * 0.875);
  }

  &.size-small {
    width: map-get(variables.$iconSizes, small);
    height: map-get(variables.$iconSizes, small);
    font-size: map-get(variables.$iconSizes, small);
  }

  &.size-xsmall {
    width: map-get(variables.$iconSizes, xsmall);
    height: map-get(variables.$iconSizes, xsmall);
    font-size: map-get(variables.$iconSizes, xsmall);
  }

  &.color-primary {
    color: map-get(variables.$colors, primary);
  }

  &.color-error {
    color: map-get(variables.$colors, error);
  }

  &.color-neutral {
    color: map-get(variables.$colors, content-normal);
  }

  &.color-dark {
    color: map-get(variables.$colors, content-dark);
  }

  &.color-dim {
    color: map-get(variables.$colors, content-dim);
  }

  &.color-on-primary {
    color: map-get(variables.$colors, on-primary);
  }

  &.color-darkest {
    color: map-get(variables.$colors, content-darkest);
  }
}

</style>
